import axios from 'axios';
import { eventAPI } from './eventAPI';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include authentication token
api.interceptors.request.use(
  (config) => {
    try {
      // Check both 'token' and 'travel-futura-token' for backward compatibility
      const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
      
      // Debug logging
      console.log('🔐 Axios Interceptor:', {
        url: config.url,
        method: config.method,
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        hasHeaders: !!config.headers,
        existingAuth: config.headers?.Authorization ? 'exists' : 'missing'
      });
      
      if (token) {
        // Ensure headers object exists
        if (!config.headers) {
          config.headers = {};
        }
        // Set Authorization header (will override any existing one)
        config.headers.Authorization = `Bearer ${token}`;
        // Also set in common headers for consistency
        config.headers.common = config.headers.common || {};
        config.headers.common.Authorization = `Bearer ${token}`;
        
        console.log('✅ Interceptor set Authorization header:', {
          headerSet: !!config.headers.Authorization,
          headerPrefix: config.headers.Authorization?.substring(0, 20) + '...'
        });
      } else {
        console.warn('⚠️ No token found in localStorage for request:', config.url);
      }
    } catch (error) {
      console.error('❌ Error in axios interceptor:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Health check
export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Upload API
export const uploadAPI = {
  // Upload multiple images
  uploadImages: async (formData) => {
    try {
      const response = await api.post('/api/v1/upload/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes timeout for large file uploads
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload feedback attachments (multiple files - images, PDFs, documents)
  uploadFeedbackFiles: async (files) => {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('attachments', file);
      });
      
      const response = await api.post('/api/v1/upload/feedback', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes timeout for large file uploads
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete uploaded file
  deleteFile: async (filename) => {
    try {
      const response = await api.delete(`/api/v1/upload/${filename}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get file info
  getFileInfo: async (filename) => {
    try {
      const response = await api.get(`/api/v1/upload/${filename}/info`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Attractions API
export const attractionsAPI = {
  // Get all attractions with optional filters, pagination, and search
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/attractions', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get filter options (categories, districts, subcategories)
  getFilterOptions: async () => {
    try {
      const response = await api.get('/api/attractions/filter-options');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get attraction by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/api/attractions/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new attraction
  create: async (attractionData) => {
    try {
      const response = await api.post('/api/attractions', attractionData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update attraction
  update: async (id, attractionData) => {
    try {
      const response = await api.put(`/api/attractions/${id}`, attractionData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete attraction
  delete: async (id) => {
    try {
      const response = await api.delete(`/api/attractions/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Media management methods
  uploadMedia: async (id, files) => {
    try {
      const formData = new FormData();
      
      // Add files to FormData
      if (Array.isArray(files)) {
        files.forEach(file => {
          formData.append('images', file);
        });
      } else {
        formData.append('images', files);
      }

      const response = await api.post(`/api/attractions/${id}/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  addMediaLink: async (id, mediaData) => {
    try {
      const response = await api.post(`/api/attractions/${id}/add-media`, mediaData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  removeMedia: async (id, mediaIndex) => {
    try {
      const response = await api.delete(`/api/attractions/${id}/media/${mediaIndex}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Currencies API
export const currenciesAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/api/currencies');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// User API
export const userAPI = {
  // Get all users
  getAll: async (limit = 50, offset = 0) => {
    try {
      const response = await api.get(`/api/v1/user?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Profile endpoints
  getProfile: async (userId = 1) => {
    try {
      const response = await api.get(`/api/v1/user/profile/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateProfile: async (userId = 1, profileData) => {
    try {
      const response = await api.put(`/api/v1/user/profile/${userId}`, profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Preferences endpoints (CRUD operations)
  getAllPreferences: async (page = 1, limit = 50) => {
    try {
      const response = await api.get(`/api/v1/user/preferences?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getPreferences: async (userId) => {
    try {
      const response = await api.get(`/api/v1/user/preferences/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createPreferences: async (preferencesData) => {
    try {
      const response = await api.post('/api/v1/user/preferences', preferencesData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updatePreferences: async (userId, preferencesData) => {
    try {
      const response = await api.put(`/api/v1/user/preferences/${userId}`, preferencesData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deletePreferences: async (userId) => {
    try {
      const response = await api.delete(`/api/v1/user/preferences/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // History endpoint
  getHistory: async (userId = 1, page = 1, limit = 20) => {
    try {
      const response = await api.get(`/api/v1/user/history/${userId}?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create user
  create: async (userData) => {
    try {
      const response = await api.post('/api/v1/user', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete user
  delete: async (userId) => {
    try {
      const response = await api.delete(`/api/v1/user/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Demo endpoint
  createDemo: async () => {
    try {
      const response = await api.post('/api/v1/user/demo');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Favorites API
export const favoritesAPI = {
  // Get all favorites
  getAll: async (userId = 1, type = null, page = 1, limit = 20) => {
    try {
      let url = `/api/v1/favorites?page=${page}&limit=${limit}`;
      if (type) url += `&type=${type}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add to favorites
  add: async (item_id, type = 'attraction', userId = 1) => {
    try {
      const response = await api.post('/api/v1/favorites', { item_id, type, userId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove from favorites
  remove: async (favoriteId) => {
    try {
      const response = await api.delete(`/api/v1/favorites/${favoriteId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get favorite by ID
  getById: async (favoriteId) => {
    try {
      const response = await api.get(`/api/v1/favorites/${favoriteId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Check if item is favorited
  checkIsFavorited: async (item_id, type = 'attraction', userId = 1) => {
    try {
      const response = await api.get(`/api/v1/favorites/check?item_id=${item_id}&type=${type}&userId=${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Notifications API
export const notificationsAPI = {
  // Get all notifications
  getAll: async (userId = 1, is_read = null, type = null, page = 1, limit = 20) => {
    try {
      let url = `/api/v1/notifications?page=${page}&limit=${limit}`;
      if (is_read !== null) url += `&is_read=${is_read}`;
      if (type) url += `&type=${type}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get notification by ID
  getById: async (notificationId, userId = 1) => {
    try {
      const response = await api.get(`/api/v1/notifications/${notificationId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark as read
  markAsRead: async (notificationId, userId = 1) => {
    try {
      const response = await api.patch(`/api/v1/notifications/${notificationId}/read`, { userId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark all as read
  markAllAsRead: async (userId = 1) => {
    try {
      const response = await api.patch('/api/v1/notifications/read-all', { userId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create notification (for testing)
  create: async (notificationData) => {
    try {
      const response = await api.post('/api/v1/notifications', notificationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Chat API
export const chatAPI = {
  // Conversation management
  getConversations: async (userId = 1, page = 1, limit = 20) => {
    try {
      const params = new URLSearchParams({
        userId: userId.toString(),
        page: page.toString(),
        limit: limit.toString()
      });
      const response = await api.get(`/api/v1/chat/conversations?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getConversationById: async (id, userId = 1) => {
    try {
      const params = new URLSearchParams({ userId: userId.toString() });
      const response = await api.get(`/api/v1/chat/conversations/${id}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  createConversation: async (data) => {
    try {
      const response = await api.post('/api/v1/chat/conversations', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  updateConversation: async (id, data) => {
    try {
      const response = await api.put(`/api/v1/chat/conversations/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteConversation: async (id, userId = 1) => {
    try {
      const params = new URLSearchParams({ userId: userId.toString() });
      const response = await api.delete(`/api/v1/chat/conversations/${id}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getConversationMessages: async (id, userId = 1, page = 1, limit = 50) => {
    try {
      const params = new URLSearchParams({
        userId: userId.toString(),
        page: page.toString(),
        limit: limit.toString()
      });
      const response = await api.get(`/api/v1/chat/conversations/${id}/messages?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Chat query and responses
  sendQuery: async (data) => {
    try {
      const response = await api.post('/api/v1/chat/query', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getSuggestions: async (language = 'en', category = null, limit = 10) => {
    try {
      const params = new URLSearchParams({
        language,
        limit: limit.toString()
      });
      
      if (category) {
        params.append('category', category);
      }
      
      const response = await api.get(`/api/v1/chat/suggestions?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// AI Chat API (for TravelFutura AI Assistant)
export const aiChatAPI = {
  // Ask a question to the AI assistant
  ask: async (data) => {
    try {
      const response = await api.post('/api/ai/ask', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get the latest active conversation for a user with messages
  getLatestConversation: async (userId) => {
    try {
      const response = await api.get(`/api/ai/latest-conversation?user_id=${encodeURIComponent(userId)}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get chat suggestions
  getSuggestions: async (language = 'en', category = null, limit = 10) => {
    try {
      const params = new URLSearchParams({
        language,
        limit: limit.toString()
      });
      
      if (category) {
        params.append('category', category);
      }
      
      const response = await api.get(`/api/ai/suggestions?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Banner Management API
export const bannersAPI = {
  // Get all banners (admin)
  getAll: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          searchParams.append(key, params[key].toString());
        }
      });
      
      const response = await api.get(`/api/v1/banners?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get active banners (public)
  getActive: async (targetPage = 'home') => {
    try {
      const response = await api.get(`/api/v1/banners/active?target_page=${targetPage}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get banner by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/api/v1/banners/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create banner (JSON)
  create: async (bannerData) => {
    try {
      const response = await api.post('/api/v1/banners', bannerData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create banner with image upload
  createWithImage: async (formData) => {
    try {
      const response = await api.post('/api/v1/banners/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update banner (JSON)
  update: async (id, bannerData) => {
    try {
      const response = await api.put(`/api/v1/banners/${id}`, bannerData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update banner with image upload
  updateWithImage: async (id, formData) => {
    try {
      const response = await api.put(`/api/v1/banners/${id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete banner
  delete: async (id) => {
    try {
      const response = await api.delete(`/api/v1/banners/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Track banner click
  trackClick: async (id) => {
    try {
      const response = await api.post(`/api/v1/banners/${id}/click`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toggle banner status
  toggleStatus: async (id) => {
    try {
      const response = await api.post(`/api/v1/banners/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reorder banners
  updatePositions: async (positions) => {
    try {
      const response = await api.put('/api/v1/banners/positions', { positions });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get banner statistics
  getStats: async () => {
    try {
      const response = await api.get('/api/v1/banners/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Home Cards Management API
export const homeCardsAPI = {
  // Get all home cards
  getAll: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          searchParams.append(key, params[key].toString());
        }
      });
      
      const response = await api.get(`/api/v1/home-cards?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get home card by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/api/v1/home-cards/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new home card
  create: async (cardData) => {
    try {
      const response = await api.post('/api/v1/home-cards', cardData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update home card
  update: async (id, cardData) => {
    try {
      const response = await api.put(`/api/v1/home-cards/${id}`, cardData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete home card
  delete: async (id) => {
    try {
      const response = await api.delete(`/api/v1/home-cards/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload home card icon
  uploadIcon: async (file) => {
    try {
      const formData = new FormData();
      formData.append('icon', file);
      const response = await api.post('/api/v1/home-cards/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Search home cards
  search: async (query, params = {}) => {
    try {
      const searchParams = new URLSearchParams({ q: query });
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          searchParams.append(key, params[key].toString());
        }
      });
      
      const response = await api.get(`/api/v1/home-cards/search?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Translation methods
  // Target languages come from tenant config server-side (no client-side list).
  // Pass { cardId } for a saved card, or { title, text_content } to translate
  // before saving. Route is mounted at /api/v1/home-cards-translation.
  translate: async (payload) => {
    try {
      // Translation runs several haiku calls server-side; override the 10s
      // default with 45s (mirrors the attraction translate path) so a batch of
      // languages does not trip the global axios timeout.
      const response = await api.post('/api/v1/home-cards-translation/translate', payload, {
        timeout: 45000
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTranslations: async (id) => {
    try {
      const response = await api.get(`/api/v1/home-cards/${id}/translations`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateTranslation: async (id, language, translationData) => {
    try {
      const response = await api.put(`/api/v1/home-cards/${id}/translations/${language}`, translationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Feedback API
export const feedbackAPI = {
  // Create feedback with optional file uploads (one-step approach)
  createFeedback: async (feedbackData, files = []) => {
    try {
      const formData = new FormData();
      
      // Add all feedback fields
      Object.keys(feedbackData).forEach(key => {
        if (feedbackData[key] !== null && feedbackData[key] !== undefined) {
          if (typeof feedbackData[key] === 'object') {
            formData.append(key, JSON.stringify(feedbackData[key]));
          } else {
            formData.append(key, feedbackData[key]);
          }
        }
      });
      
      // Add files if provided
      files.forEach(file => {
        formData.append('attachments', file);
      });
      
      const response = await api.post('/api/v1/feedback', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create feedback with pre-uploaded file URLs (two-step approach)
  createFeedbackWithUrls: async (feedbackData, attachmentUrls = []) => {
    try {
      const payload = {
        ...feedbackData,
        attachments: attachmentUrls
      };
      
      const response = await api.post('/api/v1/feedback', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all feedbacks with filters
  getAllFeedbacks: async (params = {}) => {
    try {
      const response = await api.get('/api/v1/feedback', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get feedback by ID
  getFeedbackById: async (id) => {
    try {
      const response = await api.get(`/api/v1/feedback/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update feedback (admin only)
  updateFeedback: async (id, updateData) => {
    try {
      const response = await api.put(`/api/v1/feedback/${id}`, updateData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete feedback (admin only)
  deleteFeedback: async (id) => {
    try {
      const response = await api.delete(`/api/v1/feedback/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get feedback statistics
  getStats: async () => {
    try {
      const response = await api.get('/api/v1/feedback/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Google Images API
export const googleImagesAPI = {
  search: async (query, start = 1, num = 10) => {
    try {
      const response = await api.get(
        `/api/v1/google-images/search?query=${encodeURIComponent(query)}&start=${start}&num=${num}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  downloadSelected: async (imageUrls) => {
    try {
      const response = await api.post('/api/v1/google-images/download', {
        imageUrls
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Facebook (Meta) integration - Melaka Tourism page posts → draft events
export const facebookAPI = {
  getPosts: async (limit = 25) => {
    const response = await api.get('/api/v1/facebook/posts', { params: { limit } });
    return response.data;
  },
  createEventFromPost: async (fbPostId) => {
    const response = await api.post('/api/v1/facebook/events', { fb_post_id: fbPostId });
    return response.data;
  },
};

// Export eventAPI (facebookAPI already exported above)
export { eventAPI };

// ── Itinerary Planner API ────────────────────────────────────────────────────
export const itineraryAPI = {
  // Dashboard stats
  getStats: async () => {
    const response = await api.get('/api/admin/itineraries/stats');
    return response.data;
  },

  // List itineraries with filters
  getAll: async (params = {}) => {
    const response = await api.get('/api/admin/itineraries', { params });
    return response.data;
  },

  // Single itinerary detail
  getById: async (id) => {
    const response = await api.get(`/api/admin/itineraries/${id}`);
    return response.data;
  },

  // Archive an itinerary
  archive: async (id) => {
    const response = await api.patch(`/api/admin/itineraries/${id}/archive`);
    return response.data;
  },

  // Test generate (testing console)
  testGenerate: async (payload) => {
    const response = await api.post('/api/admin/itineraries/test-generate', payload, {
      timeout: 60000,
    });
    return response.data;
  },

  // User itineraries
  getByUser: async (userId, params = {}) => {
    const response = await api.get(`/api/admin/users/${userId}/itineraries`, { params });
    return response.data;
  },
};

// ── Preset API ───────────────────────────────────────────────────────────────
export const presetAPI = {
  getAll: async () => {
    const response = await api.get('/api/admin/presets');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/admin/presets/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/admin/presets/${id}`, data);
    return response.data;
  },

  addAttraction: async (id, data) => {
    const response = await api.post(`/api/admin/presets/${id}/attractions`, data);
    return response.data;
  },

  removeAttraction: async (id, attractionId) => {
    const response = await api.delete(`/api/admin/presets/${id}/attractions/${attractionId}`);
    return response.data;
  },

  fixAttraction: async (id, attractionId) => {
    const response = await api.post(`/api/admin/presets/${id}/attractions/${attractionId}/fix`);
    return response.data;
  },
};

export default api;